import { useState, useEffect, useRef } from 'react';
import { formatFileSize } from '../../../../components/FormField';
import { type FileMeta, type OutputFormat, compressImage } from './imageCompression';

interface CompressionModalProps {
  meta: FileMeta;
  file: File;
  onSave: (patch: Pick<FileMeta, 'quality' | 'format'>) => void;
  onClose: () => void;
}

export default function CompressionModal({ meta, file, onSave, onClose }: CompressionModalProps) {
  const [quality, setQuality] = useState(meta.quality);
  const [format, setFormat] = useState<OutputFormat>(meta.format);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewSize, setPreviewSize] = useState<number | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestPreviewUrl = useRef<string | null>(null);

  const buildPreview = async (q: number, f: OutputFormat) => {
    setPreviewing(true);
    try {
      const compressed = await compressImage(file, q / 100, f);
      const url = URL.createObjectURL(compressed);
      if (latestPreviewUrl.current) URL.revokeObjectURL(latestPreviewUrl.current);
      latestPreviewUrl.current = url;
      setPreviewUrl(url);
      setPreviewSize(compressed.size);
    } finally {
      setPreviewing(false);
    }
  };

  useEffect(() => {
    buildPreview(quality, format);
    return () => { if (latestPreviewUrl.current) URL.revokeObjectURL(latestPreviewUrl.current); };
  }, []);

  const schedulePreview = (q: number, f: OutputFormat) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => buildPreview(q, f), 300);
  };

  const handleQuality = (v: number) => { setQuality(v); schedulePreview(v, format); };
  const handleFormat = (f: OutputFormat) => { setFormat(f); schedulePreview(quality, f); };

  const sizeDelta = previewSize !== null
    ? previewSize < file.size
      ? `-${Math.round((1 - previewSize / file.size) * 100)}%`
      : `+${Math.round((previewSize / file.size - 1) * 100)}%`
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="shadow-xl w-full mx-4 max-h-[90vh] overflow-y-auto" style={{ maxWidth: 480, backgroundColor: '#f5f5f5' }}>

        <div style={{ padding: '8px 12px', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>
          Compression settings — {file.name}
        </div>

        <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>

          <div>
            <div className="text-xs font-bold mb-1">Output format</div>
            <div className="flex gap-2">
              {(['webp', 'jpeg', 'png'] as OutputFormat[]).map((f) => (
                <button key={f} type="button" onClick={() => handleFormat(f)} className={`btn btn-sm ${format === f ? 'btn-primary' : 'btn-ghost'}`}>
                  {f.toUpperCase()}
                </button>
              ))}
            </div>
            {format === 'png' && (
              <div className="text-xs text-base-content/60 mt-1">PNG is lossless — quality slider has no effect.</div>
            )}
          </div>

          <div>
            <div className="text-xs font-bold mb-1">
              Quality: <span className="text-primary">{format === 'png' ? 'lossless' : `${quality}%`}</span>
            </div>
            <input
              type="range" min={1} max={100} value={quality} disabled={format === 'png'}
              onChange={(e) => handleQuality(Number(e.target.value))}
              className="range range-sm range-primary w-full"
            />
            <div className="flex justify-between text-xs text-base-content/50">
              <span>1% (smallest)</span><span>100% (best)</span>
            </div>
          </div>

          <div className="flex gap-4 text-sm">
            <div>
              <span className="text-base-content/60">Original: </span>
              <span className="font-medium">{formatFileSize(file.size)}</span>
            </div>
            {previewSize !== null && sizeDelta !== null && (
              <div>
                <span className="text-base-content/60">Compressed: </span>
                <span className={`font-medium ${previewSize < file.size ? 'text-success' : 'text-warning'}`}>
                  {formatFileSize(previewSize)} ({sizeDelta})
                </span>
              </div>
            )}
          </div>

          <div style={{ position: 'relative', backgroundColor: '#ddd', minHeight: 120 }}>
            {previewUrl && (
              <img src={previewUrl} alt="preview" style={{ width: '100%', maxHeight: 240, objectFit: 'contain', display: 'block' }} />
            )}
            {previewing && (
              <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', backgroundColor: 'rgba(255,255,255,0.6)' }}>
                <span className="loading loading-spinner loading-sm" />
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: '8px 12px', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
          <button type="button" className="btn btn-sm" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-sm btn-primary" onClick={() => { onSave({ quality, format }); onClose(); }}>
            Apply
          </button>
        </div>

      </div>
    </div>
  );
}
